import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { type RootState } from '.';
import { fetchPlans as makePlansRequest } from '@/utils/api/plans';
import { showErrorNotification } from '@/store/new-order/notificationsSlice';
import { selectPlanAndProceed } from '@/store/new-order/actions/planSelect';

import { type Plan, mapProductsArray } from '@/utils/mapProductObject';
import { filterOutWeeklyPlans } from '@/utils/planFilters';

// caused by design, no more than 3 fit into a panel
const MAX_DISPLAYED_PLANS = 3;

const onPlanPanelToggleChanged = (fetchedPlans: Plan[]): Plan[] => {
  // Will leave this here if toggle is brought back
  // let plans: Plan[];
  // if (isToggleChecked) {
  //   plans = fetchedPlans.slice(MAX_DISPLAYED_PLANS, MAX_DISPLAYED_PLANS + MAX_DISPLAYED_PLANS);
  // } else {
  //   plans = fetchedPlans.slice(0, MAX_DISPLAYED_PLANS);
  // }
  // return plans.slice(0, MAX_DISPLAYED_PLANS).sort((a, b) => a.index - b.index);

  return fetchedPlans.slice(0, MAX_DISPLAYED_PLANS);
};

export interface PlansState {
  isPlansLoading: boolean;

  /** Plans that were received from API */
  fetchedPlans: Plan[];
  /** Plans that were filtered to be disaplayed on PlansScreen */
  displayedPlans: Plan[];

  // not used for now
  isPlanPanelToggleChecked: boolean;

  selectedPlan?: Plan;
  planLoadingError?: string;
}

const initialState: PlansState = {
  isPlansLoading: true,
  fetchedPlans: [],
  displayedPlans: [],
  selectedPlan: undefined,

  isPlanPanelToggleChecked: true,
};

export const fetchPlans = createAsyncThunk<Plan[], undefined, { state: RootState }>(
  'fetchPlans',
  async (arg, { dispatch, getState }) => {
    try {
      return await makePlansRequest().then((res) => {
        if (res.products.length < 1) {
          throw new Error('Zero plans received from API');
        }

        const plans = mapProductsArray(res.products);

        return getState().featureFlags.excludeWeeklyPlans ? filterOutWeeklyPlans(plans) : plans;
      });
    } catch (err) {
      console.error(err);
      void dispatch(showErrorNotification('We couldn’t fetch plans for you, please try again later'));

      throw err;
    }
  },
);

export const plansSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {
    togglePlansPanelCheck: (state) => {
      state.isPlanPanelToggleChecked = !state.isPlanPanelToggleChecked;
      state.displayedPlans = onPlanPanelToggleChanged(state.fetchedPlans);
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchPlans.pending, (state) => {
      state.isPlansLoading = true;
    });
    builder.addCase(fetchPlans.fulfilled, (state, action) => {
      state.fetchedPlans = action.payload;
      state.displayedPlans = onPlanPanelToggleChanged(action.payload);
      state.isPlansLoading = false;
    });
    builder.addCase(fetchPlans.rejected, (state, action) => {
      state.planLoadingError = action.error.message;
    });

    builder.addCase(selectPlanAndProceed, (state, action) => {
      state.selectedPlan = action.payload;
    });
  },
});

export const { togglePlansPanelCheck } = plansSlice.actions;
